import { Button } from '@mantine/core';
import { HotkeyItem, useEventListener, useHotkeys } from '@mantine/hooks';
import useCarousel from 'embla-carousel-react';
import { Children, FC, ReactElement, ReactNode, useEffect, useRef, useState } from 'react';

import { cn } from '@/utils/styleUtils.ts';

interface Props {
    children: ReactNode;
    isVertical?: boolean;
    isInfinite?: boolean;
    isCentered?: boolean;
    hasArrows?: boolean;
    prevButton?: ReactNode;
    nextButton?: ReactNode;
    gap?: number;
    slidesToScroll?: number;
    slidesToShow?: number;
    currentIndex?: number;
    classNames?: {
        root?: string;
        wrapper?: string;
        card?: string;
        controls?: {
            container?: string;
            disabled?: string;
            previous?: string;
            next?: string;
        };
    };
    enableKeyboardNavigation?: boolean;
    enableWheelNavigation?: boolean;
    onScroll?: (index?: number) => void;
}

const CardFlipper: FC<Props> = ({
    children,
    isVertical = false,
    isInfinite = false,
    isCentered = false,
    gap = 0,
    slidesToScroll,
    slidesToShow = 1,
    currentIndex = 0,
    classNames,
    hasArrows = false,
    prevButton = 'Previous',
    nextButton = 'Next',
    enableKeyboardNavigation = false,
    enableWheelNavigation = false,
    onScroll,
}) => {
    const [ref, api] = useCarousel({
        loop: isInfinite,
        axis: isVertical ? 'y' : 'x',
        slidesToScroll: slidesToScroll ?? 'auto',
        align: isCentered ? 'center' : 'start',
    });
    const [disabled, setDisabled] = useState({
        prev: false,
        next: false,
    });

    const keys: HotkeyItem[] = isVertical
        ? [
              ['ArrowUp', (): void => scrollPrev()],
              ['ArrowDown', (): void => scrollNext()],
          ]
        : [
              ['ArrowLeft', (): void => scrollPrev()],
              ['ArrowRight', (): void => scrollNext()],
          ];

    useHotkeys(enableKeyboardNavigation ? keys : []);

    const scrollPrev = (): void => {
        api?.scrollPrev();
        onScroll?.(api?.selectedScrollSnap());
    };

    const scrollNext = (): void => {
        api?.scrollNext();
        onScroll?.(api?.selectedScrollSnap());
    };

    useEffect(() => {
        api?.scrollTo(currentIndex);
    }, [currentIndex, api]);

    useEffect(() => {
        if (!api) return undefined;

        const checkIfDisabled = (): void => {
            setDisabled({ prev: !api.canScrollPrev(), next: !api.canScrollNext() });
        };

        checkIfDisabled();
        api.on('select', checkIfDisabled);
        api.on('reInit', checkIfDisabled);

        return (): void => {
            api.off('select', checkIfDisabled);
            api.off('reInit', checkIfDisabled);
        };
    }, [api]);

    const accumulated_delta = useRef(0);

    const wheelRef = useEventListener(
        'wheel',
        (e: WheelEvent) => {
            if (!enableWheelNavigation) return;

            e.preventDefault();

            const delta = isVertical ? e.deltaY : e.deltaX;
            accumulated_delta.current += delta;

            if (accumulated_delta.current > 50) {
                scrollNext();
                accumulated_delta.current = 0;
            } else if (accumulated_delta.current < -50) {
                scrollPrev();
                accumulated_delta.current = 0;
            }
        },
        { passive: false }
    );

    return (
        <div className={cn('relative', classNames?.root)}>
            <div className="h-full w-full overflow-hidden" ref={ref}>
                <div
                    ref={wheelRef}
                    className={cn(
                        'flex',
                        isVertical && `flex-col`,
                        isVertical ? `-my-${gap / 2}` : `-mx-${gap / 2}`,
                        classNames?.wrapper
                    )}
                >
                    {Children.toArray(children).map((child) => (
                        <div
                            key={`wrapper_${(child as ReactElement).key}`}
                            className={cn(
                                `flex-shrink-0 basis-[${100 / slidesToShow}%]`,
                                isVertical ? `py-${gap / 2}` : `px-${gap / 2}`,
                                classNames?.card
                            )}
                        >
                            {child}
                        </div>
                    ))}
                </div>
            </div>
            {hasArrows && (
                <div className={cn(classNames?.controls?.container)}>
                    <Button
                        onClick={scrollPrev}
                        disabled={disabled.prev}
                        className={cn(classNames?.controls?.previous, disabled.prev && classNames?.controls?.disabled)}
                    >
                        {prevButton}
                    </Button>
                    <Button
                        onClick={scrollNext}
                        disabled={disabled.next}
                        className={cn(classNames?.controls?.next, disabled.next && classNames?.controls?.disabled)}
                    >
                        {nextButton}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default CardFlipper;
